import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import toast from 'react-hot-toast';
import { NavigateFunction } from 'react-router-dom';

import {
  AddProjectMemberPayload,
  CreateProjectResponse,
  ProjectDetailResponse,
  UpdateProjectMemberPayload,
  addExpertToProject,
  addMemberToProject,
  assignProjectApi,
  createAndPatchClient,
  createMeetingSlots,
  createProject,
  createProjectRole,
  deleteMembersOfProject,
  deleteProject,
  deleteProjectClient,
  deleteProjectExpert,
  deleteProjectRole,
  generateFilters,
  generateMeeting,
  getApprovedExperts,
  getMembersOfProject,
  getProjectById,
  getProjectCreatePermissions,
  getProjectExperts,
  getProjectRoles,
  getProjects,
  getSharedProjectById,
  getSuggestedTenants,
  getUnassignedProjects,
  getUnscheduledMeetings,
  searchPeoples,
  unassignProjectApi,
  updateExpert,
  updateMemberToProject,
  updateProject,
  updateProjectExpert,
  updateProjectRole,
} from '@/api';
// Updated API calls
import type { ProjectExpertSummary, ProjectExpertsQuery, ProjectExpertsResponse } from '@/api';

// import { companies } from '@/pages/dashboard/project/detail/data/data';
import { Tenant } from '@/interface';
import {
  CreateClientPayload,
  CreateMeetingSlotsResponse,
  CreateProjectForm,
  Expert,
  ExpertFormData,
  GenerateFilterForm,
  GetProjectInterface,
  Project,
  ProjectMember,
  ProjectRole,
  UnscheduledMeetingSlot,
  // UpdateProjectExpertFormData,
  UpdateProjectForm,
  createMeetingSlotsFrom,
  createRoleFrom,
  generateMeetingForm,
} from '@/interface';

interface ProjectErrorPayload {
  message: string;
  [key: string]: unknown;
}

const toProjectErrorPayload = (error: unknown, fallbackMessage: string): ProjectErrorPayload => {
  const data = axios.isAxiosError(error) ? (error.response?.data as Partial<ProjectErrorPayload> | undefined) : undefined;
  return { ...data, message: data?.message || fallbackMessage };
};

const toErrorMessage = (error: unknown, fallbackMessage: string): string => {
  const message = axios.isAxiosError(error) ? (error.response?.data as { message?: string } | undefined)?.message : undefined;
  return message || fallbackMessage;
};

interface ProjectState {
  loading: boolean;
  error: ProjectErrorPayload | null;
  filters: unknown;
  searchResults: unknown[];
  projects: GetProjectInterface[];
  projectsRoles: ProjectRole[];
  projectDetail: Project | null;
  projectExperts: ProjectExpertSummary[];
  projectExpertsProjectId: string | null;
  projectExpertsTotal: number;
  projectExpertsPage: number;
  projectExpertsLimit: number;
  projectExpertsTotalPages: number;
  projectMembers: ProjectMember[];
  allPermisionKeys: string[];
  tenants: Tenant[];
  unassignedProjects: Project[] | undefined;
  assignedProjects: Project[];
  loadingUnassigned: boolean;
  errorUnassigned: string | null;
  loadingAssign: boolean;
  loadingExperts: boolean;
  createmeetingSlots: {
    loading: boolean;
    success: boolean;
    error: string | null;
    data: CreateMeetingSlotsResponse | null;
  };
  getUnscheduledMeetingList: {
    loading: boolean;
    success: boolean;
    error: string | null;
    data: UnscheduledMeetingSlot[];
  };
  generateMeeting: {
    loading: boolean;
    success: boolean;
    error: string | null;
  };
}

const initialState: ProjectState = {
  loading: false,
  error: null,
  filters: null,
  searchResults: [],
  projectsRoles: [],
  projects: [],
  projectDetail: null,
  projectExperts: [],
  projectExpertsProjectId: null,
  projectExpertsTotal: 0,
  projectExpertsPage: 1,
  projectExpertsLimit: 20,
  projectExpertsTotalPages: 0,
  projectMembers: [],
  allPermisionKeys: [],
  tenants: [],
  unassignedProjects: undefined,
  assignedProjects: [],
  loadingUnassigned: false,
  errorUnassigned: null,
  loadingAssign: false,
  loadingExperts: false,
  createmeetingSlots: { loading: false, success: false, error: null, data: null },
  getUnscheduledMeetingList: { loading: false, success: false, error: null, data: [] },
  generateMeeting: { loading: false, success: false, error: null },
};

// Generate Filter Request
export const generateFilterRequest = createAsyncThunk<
  unknown,
  { data: GenerateFilterForm },
  { rejectValue: ProjectErrorPayload }
>('project/generateFilterRequest', async ({ data }, { rejectWithValue }) => {
  try {
    const response = await generateFilters(data);
    return response.data;
  } catch (error) {
    return rejectWithValue(toProjectErrorPayload(error, 'Failed to generate filters'));
  }
});
export const addMemberToProjectRequest = createAsyncThunk<
  unknown,
  { data: AddProjectMemberPayload },
  { rejectValue: ProjectErrorPayload }
>('project/addMemberToProject', async ({ data }, { rejectWithValue }) => {
  try {
    const response = await addMemberToProject(data);
    return response.data;
  } catch (error) {
    return rejectWithValue(toProjectErrorPayload(error, 'Failed to add member'));
  }
});
export const updateMemberToProjectRequest = createAsyncThunk<
  unknown,
  { id: string; data: UpdateProjectMemberPayload },
  { rejectValue: ProjectErrorPayload }
>('project/updateMemberToProject', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await updateMemberToProject(id, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(toProjectErrorPayload(error, 'Failed to add member'));
  }
});

// Search People Request
export const searchPeopleRequest = createAsyncThunk<
  unknown[],
  { data: Record<string, unknown> },
  { rejectValue: ProjectErrorPayload }
>('project/searchPeople', async ({ data }, { rejectWithValue }) => {
  try {
    const response = await searchPeoples(data);
    return response.data.people;
  } catch (error) {
    return rejectWithValue(toProjectErrorPayload(error, 'Failed to search people'));
  }
});
export const getProjectCreatePermissionsRequest = createAsyncThunk<string[], void, { rejectValue: ProjectErrorPayload }>(
  'project/PermissionKeys',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { project: ProjectState };

      if (state.project.allPermisionKeys.length > 0) {
        return state.project.allPermisionKeys;
      }

      const response = await getProjectCreatePermissions();
      return response.data;
    } catch (error) {
      return rejectWithValue(toProjectErrorPayload(error, 'Failed to search people'));
    }
  },
);

export const createProjectRequest = createAsyncThunk<
  CreateProjectResponse,
  { data: CreateProjectForm },
  { rejectValue: ProjectErrorPayload }
>('project', async ({ data }, { rejectWithValue }) => {
  try {
    const response = await createProject(data);
    return response.data;
  } catch (error) {
    return rejectWithValue(toProjectErrorPayload(error, 'Failed to create project'));
  }
});
export const createAndPatchClientRequest = createAsyncThunk<
  { client: Project['client']; projectId: string },
  CreateClientPayload,
  { rejectValue: ProjectErrorPayload }
>('client/createAndPatchClient', async ({ projectId, clientData }, { rejectWithValue }) => {
  try {
    const response = await createAndPatchClient(projectId, clientData);
    return response.data;
  } catch (error) {
    return rejectWithValue(toProjectErrorPayload(error, 'Failed to create or update client'));
  }
});
export const deleteProjectClientRequest = createAsyncThunk<
  { projectId: string },
  { projectId: string },
  { rejectValue: ProjectErrorPayload }
>('client/deleteProjectClient', async ({ projectId }, { rejectWithValue }) => {
  try {
    const response = await deleteProjectClient(projectId);
    return response.data;
  } catch (error) {
    return rejectWithValue(toProjectErrorPayload(error, 'Failed to delete client'));
  }
});

export const updateProjectRequest = createAsyncThunk<
  Project,
  { id: string; data: UpdateProjectForm },
  { rejectValue: ProjectErrorPayload }
>('updateProject', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await updateProject(id, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(toProjectErrorPayload(error, 'Failed to create project'));
  }
});
export const deleteProjectRequest = createAsyncThunk<{ id: string }, { id: string }, { rejectValue: ProjectErrorPayload }>(
  'deleteProjectRequest',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await deleteProject(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(toProjectErrorPayload(error, 'Failed to create project'));
    }
  },
);
export const deleteProjectExpertRequest = createAsyncThunk<{ id: string }, { id: string }, { rejectValue: ProjectErrorPayload }>(
  'deleteProjectExpertRequest',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await deleteProjectExpert(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(toProjectErrorPayload(error, 'Failed to create project'));
    }
  },
);
export const addExpertToProjectRequest = createAsyncThunk<
  unknown,
  { data: ExpertFormData; navigate: NavigateFunction },
  { rejectValue: ProjectErrorPayload }
>('addExpertToProjectRequest', async ({ data, navigate }, { rejectWithValue, dispatch }) => {
  try {
    const response = await addExpertToProject(data);
    if (data.projectId) {
      dispatch(getProjectExpertsRequest(data.projectId));
    }
    navigate(-1);
    return response.data;
  } catch (error) {
    return rejectWithValue(toProjectErrorPayload(error, 'Failed to create project'));
  }
});
export const updateExpertRequest = createAsyncThunk<
  unknown,
  { id: string; data: ExpertFormData; navigate: NavigateFunction },
  { rejectValue: ProjectErrorPayload }
>('updateExpertRequest', async ({ id, data, navigate }, { rejectWithValue }) => {
  try {
    const response = await updateExpert(id, data);
    navigate(-1);
    return response.data;
  } catch (error) {
    return rejectWithValue(toProjectErrorPayload(error, 'Failed to create project'));
  }
});
export const updateProjectExpertRequest = createAsyncThunk<
  unknown,
  { id: string; projectId?: string; data: ExpertFormData; navigate: NavigateFunction },
  { rejectValue: ProjectErrorPayload }
>('updateProjectExpertRequest', async ({ id, projectId, data, navigate }, { rejectWithValue, dispatch }) => {
  try {
    const response = await updateProjectExpert(id, projectId, data);
    if (projectId) {
      dispatch(getProjectDetailById(projectId));
      dispatch(getProjectExpertsRequest(projectId));
    }
    navigate(-1);
    return response.data;
  } catch (error) {
    return rejectWithValue(toProjectErrorPayload(error, 'Failed to create project'));
  }
});

export const getProjectsRequest = createAsyncThunk<
  GetProjectInterface[],
  boolean | undefined,
  { rejectValue: ProjectErrorPayload }
>('project/getProjects', async (forceRefresh = false, { rejectWithValue, getState }) => {
  try {
    const state = getState() as { project: ProjectState };

    if (!forceRefresh && state.project.projects.length > 0) {
      // Already have projects, return them unless forced
      return state.project.projects;
    }

    const response = await getProjects();
    return response.data;
  } catch (error) {
    return rejectWithValue(toProjectErrorPayload(error, 'Failed to fetch projects'));
  }
});
export const getProjectsRolesRequest = createAsyncThunk<ProjectRole[], boolean | undefined, { rejectValue: ProjectErrorPayload }>(
  'project/getProjectsRoles',
  async (forceRefresh = false, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { project: ProjectState };

      if (!forceRefresh && state.project.projectsRoles.length > 0) {
        // Already have projects, return them unless forced
        return state.project.projectsRoles;
      }

      const response = await getProjectRoles();
      return response.data;
    } catch (error) {
      return rejectWithValue(toProjectErrorPayload(error, 'Failed to fetch projects'));
    }
  },
);

export const getProjectMembersRequest = createAsyncThunk<ProjectMember[], string, { rejectValue: ProjectErrorPayload }>(
  'projectMember/getMembers',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getMembersOfProject(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(toProjectErrorPayload(error, 'Failed to fetch project details'));
    }
  },
);
export const deleteProjectMember = createAsyncThunk<{ id: string }, string, { rejectValue: ProjectErrorPayload }>(
  'project/deleteMember',
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteMembersOfProject(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(toProjectErrorPayload(error, 'Failed to delete project member'));
    }
  },
);
export const getProjectDetailById = createAsyncThunk<ProjectDetailResponse, string, { rejectValue: ProjectErrorPayload }>(
  'project/getProjectDetailById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getProjectById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(toProjectErrorPayload(error, 'Failed to fetch project details'));
    }
  },
);

export const getProjectExpertsRequest = createAsyncThunk<
  ProjectExpertsResponse,
  string | ({ projectId: string } & ProjectExpertsQuery),
  { rejectValue: ProjectErrorPayload }
>('project/getProjectExperts', async (arg, { rejectWithValue, getState }) => {
  try {
    const state = getState() as { project: ProjectState };
    const projectId = typeof arg === 'string' ? arg : arg.projectId;
    const query: ProjectExpertsQuery =
      typeof arg === 'string'
        ? {
            page: state.project.projectExpertsPage,
            limit: state.project.projectExpertsLimit,
          }
        : {
            page: arg.page ?? state.project.projectExpertsPage,
            limit: arg.limit ?? state.project.projectExpertsLimit,
            status: arg.status,
          };

    const response = await getProjectExperts(projectId, query);
    return response.data;
  } catch (error) {
    return rejectWithValue(toProjectErrorPayload(error, 'Failed to fetch project experts'));
  }
});

export const getSharedProjectDetailById = createAsyncThunk<ProjectDetailResponse, string, { rejectValue: ProjectErrorPayload }>(
  'project/getPublicProjectDetailById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getSharedProjectById(id);
      const project = response.data;
      return {
        ...project,
        title: project.title || project.name || '', // ensure `.title` exists
      };
    } catch (error) {
      return rejectWithValue(toProjectErrorPayload(error, 'Failed to fetch project details'));
    }
  },
);

//create role
export const createProjectRoleRequest = createAsyncThunk<unknown, { data: createRoleFrom }, { rejectValue: ProjectErrorPayload }>(
  'roles/createRoleRequest',
  async ({ data }, { rejectWithValue }) => {
    try {
      const response = await createProjectRole(data);
      toast.success('Role Created successfully');

      return response.data;
    } catch (error) {
      return rejectWithValue(toProjectErrorPayload(error, 'Created role failed'));
    }
  },
);
export const updateProjectRoleRequest = createAsyncThunk<
  unknown,
  { id: string; data: createRoleFrom },
  { rejectValue: ProjectErrorPayload }
>('roles/updateProjectRoleRequest', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await updateProjectRole(id, data);
    toast.success('Role updated successfully');
    return response.data;
  } catch (error) {
    return rejectWithValue(toProjectErrorPayload(error, 'Update role failed'));
  }
});
export const deleteProjectRoleRequest = createAsyncThunk<{ id?: string }, string, { rejectValue: ProjectErrorPayload }>(
  'roles/deleteProjectRoleRequest',
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteProjectRole(id);
      toast.success('Role deleted successfully');
      return response.data;
    } catch (error) {
      return rejectWithValue(toProjectErrorPayload(error, 'Delete role failed'));
    }
  },
);
export const createMeetingSlotsRequest = createAsyncThunk<
  CreateMeetingSlotsResponse,
  { data: createMeetingSlotsFrom },
  { rejectValue: string }
>('meeting/createMeetingSlotsRequest', async ({ data }, { rejectWithValue }) => {
  try {
    const response = await createMeetingSlots(data);
    toast.success('Slots Created successfully');

    return response.data;
  } catch (error) {
    return rejectWithValue(toErrorMessage(error, 'Failed'));
  }
});
export const generateMeetingRequest = createAsyncThunk<
  { authUrl?: string },
  { id: string; data: generateMeetingForm },
  { rejectValue: string }
>('meeting/generateMeetingRequest', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await generateMeeting(id, data);
    toast.success('Slots Created successfully');

    return response.data;
  } catch (error) {
    return rejectWithValue(toErrorMessage(error, 'Failed'));
  }
});

export const getUnscheduledMeetingsRequest = createAsyncThunk<
  { data: UnscheduledMeetingSlot[] },
  { id: string; date: string },
  { rejectValue: string }
>('meeting/getUnscheduledMeetingsRequest', async ({ id, date }, { rejectWithValue }) => {
  try {
    const response = await getUnscheduledMeetings(id, date);
    return response.data;
  } catch (error) {
    return rejectWithValue(toErrorMessage(error, 'Failed to create project'));
  }
});

export const getApprovedExpertsRequest = createAsyncThunk<Expert[], { id: string }, { rejectValue: ProjectErrorPayload }>(
  'getApprovedExpertsRequest',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await getApprovedExperts(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(toProjectErrorPayload(error, 'Failed to create project'));
    }
  },
);

export const fetchSuggestedTenants = createAsyncThunk<
  Tenant[],
  string,
  { rejectValue: string; state: { project: ProjectState } }
>('project/fetchSuggestedTenants', async (projectId, { rejectWithValue, getState }) => {
  try {
    const state = getState() as { project: ProjectState };
    const projectDetail = state.project.projectDetail;

    const relevantProjectDetail = projectDetail?.id === projectId ? projectDetail : undefined;
    const tenants = await getSuggestedTenants(projectId, relevantProjectDetail);
    return tenants;
  } catch (error) {
    return rejectWithValue(toErrorMessage(error, 'Failed to fetch suggested tenants'));
  }
});

export const fetchUnassignedProjects = createAsyncThunk<Project[], void, { rejectValue: string }>(
  'project/fetchUnassignedProjects',
  async (_, { rejectWithValue }) => {
    try {
      const projects = await getUnassignedProjects();
      return projects;
    } catch (error) {
      return rejectWithValue(toErrorMessage(error, 'Failed to fetch unassigned projects'));
    }
  },
);
export const assignProject = createAsyncThunk<Project, { projectId: string; companyId?: string }, { rejectValue: string }>(
  'project/assignProject',
  async ({ projectId, companyId }, { rejectWithValue }) => {
    try {
      const data = await assignProjectApi(projectId, companyId);
      return data;
    } catch (error) {
      return rejectWithValue(toErrorMessage(error, 'Failed to assign project'));
    }
  },
);

export const unassignProject = createAsyncThunk<Project, { projectId: string }, { rejectValue: string }>(
  'project/unassignProject',
  async ({ projectId }, { rejectWithValue }) => {
    try {
      const data = await unassignProjectApi(projectId);
      return data;
    } catch (error) {
      return rejectWithValue(toErrorMessage(error, 'Failed to unassign project'));
    }
  },
);

// Project Slice
export const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    resetProjectSessionState: (state) => {
      state.error = null;
      state.filters = null;
      state.searchResults = [];
      state.projects = [];
      state.projectDetail = null;
      state.projectExperts = [];
      state.projectExpertsProjectId = null;
      state.projectExpertsTotal = 0;
      state.projectExpertsPage = 1;
      state.projectExpertsLimit = 20;
      state.projectExpertsTotalPages = 0;
      state.projectMembers = [];
      state.tenants = [];
      state.unassignedProjects = undefined;
      state.assignedProjects = [];
      state.loadingUnassigned = false;
      state.errorUnassigned = null;
      state.loadingAssign = false;
      state.loadingExperts = false;
    },
    resetError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle Generate Filter Request
      .addCase(generateFilterRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateFilterRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.filters = action.payload;
      })
      .addCase(generateFilterRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      // Handle Search People Request
      .addCase(searchPeopleRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchPeopleRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = action.payload;
      })
      .addCase(searchPeopleRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      }) // Handle Create Project Request
      .addCase(createProjectRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProjectRequest.fulfilled, (state) => {
        state.loading = false;
        // You can optionally push the created project somewhere if needed
        // e.g., state.projects.push(action.payload);
      })
      .addCase(createProjectRequest.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload?.message || 'Failed to create project');
        state.error = action.payload ?? null;
      })
      .addCase(getProjectsRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProjectsRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload;
      })
      .addCase(getProjectsRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(getProjectsRolesRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProjectsRolesRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.projectsRoles = action.payload;
      })
      .addCase(getProjectsRolesRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(getProjectDetailById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProjectDetailById.fulfilled, (state, action) => {
        state.loading = false;
        const projectDetail = { ...action.payload };
        delete projectDetail.experts;
        delete projectDetail.projectExperts;
        state.projectDetail = projectDetail as Project; // Store the fetched project detail
      })
      .addCase(getProjectDetailById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(getProjectExpertsRequest.pending, (state) => {
        state.loadingExperts = true;
        state.error = null;
      })
      .addCase(getProjectExpertsRequest.fulfilled, (state, action) => {
        state.loadingExperts = false;
        const { projectId, experts, total, page, limit, totalPages } = action.payload;
        const paginatedExperts = experts || [];
        const safeTotal = Number(total) || 0;
        const safePage = Number(page) || 1;
        const safeLimit = Number(limit) || paginatedExperts.length || state.projectExpertsLimit;
        state.projectExperts = paginatedExperts;
        state.projectExpertsProjectId = projectId || null;
        state.projectExpertsTotal = safeTotal;
        state.projectExpertsPage = safePage;
        state.projectExpertsLimit = safeLimit;
        state.projectExpertsTotalPages = totalPages ?? (safeLimit > 0 ? Math.ceil(safeTotal / safeLimit) : 0);
      })
      .addCase(getProjectExpertsRequest.rejected, (state, action) => {
        state.loadingExperts = false;
        state.error = action.payload ?? null;
      })
      .addCase(getSharedProjectDetailById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSharedProjectDetailById.fulfilled, (state, action) => {
        state.loading = false;
        const projectDetail = { ...action.payload };
        delete projectDetail.experts;
        delete projectDetail.projectExperts;
        state.projectDetail = projectDetail as Project; // store the fetched public project
      })
      .addCase(getSharedProjectDetailById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(addMemberToProjectRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addMemberToProjectRequest.fulfilled, (state) => {
        state.loading = false;
        // Optionally show success message in middleware/component
      })
      .addCase(addMemberToProjectRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(updateMemberToProjectRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMemberToProjectRequest.fulfilled, (state) => {
        state.loading = false;
        // Optionally show success message in middleware/component
      })
      .addCase(updateMemberToProjectRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })

      // Handle Add Expert to Project Request
      .addCase(addExpertToProjectRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addExpertToProjectRequest.fulfilled, (state) => {
        state.loading = false;
        toast.success('Expert added successfully!');
        // Optionally handle the expert data if needed
        // For example, you might want to add the expert to a list of experts in the state
        // e.g., state.experts.push(action.payload);
      })
      .addCase(addExpertToProjectRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
        toast.error(action.payload?.message || 'Failed to add expert');
      })
      .addCase(updateExpertRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateExpertRequest.fulfilled, (state) => {
        state.loading = false;
        toast.success('Expert updated successfully');
      })
      .addCase(updateExpertRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
        toast.error(action.payload?.message || 'Failed to update expert');
      })
      // Handle Get Project Members
      .addCase(getProjectMembersRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProjectMembersRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.projectMembers = action.payload;
      })
      .addCase(getProjectMembersRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      // get all project create permissions

      .addCase(getProjectCreatePermissionsRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProjectCreatePermissionsRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.allPermisionKeys = action.payload;
      })
      .addCase(getProjectCreatePermissionsRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })

      .addCase(deleteProjectMember.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProjectMember.fulfilled, (state, action) => {
        state.loading = false;

        state.projectMembers = state.projectMembers.filter((member) => member.id !== action.payload.id);
      })
      .addCase(deleteProjectMember.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(updateProjectRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProjectRequest.fulfilled, (state, action) => {
        state.loading = false;

        // Update the specific project in the projects list if needed
        const updatedProject = { ...action.payload };
        const index = state.projects.findIndex((p) => p.id === updatedProject.id);

        if (index !== -1) {
          state.projects[index] = updatedProject;
        }
        if (state.projectDetail && state.projectDetail.id === updatedProject.id) {
          state.projectDetail = { ...state.projectDetail, ...updatedProject };
        }
        toast.success('Project updated successfully');
      })
      .addCase(updateProjectRequest.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload?.message || 'Failed to update project');
        state.error = action.payload ?? null;
      })
      .addCase(deleteProjectRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProjectRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = state.projects.filter((project) => project.id !== action.payload.id);
        toast.success('Project deleted successfully');
      })
      .addCase(deleteProjectRequest.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload?.message || 'Failed to delete project');
        state.error = action.payload ?? null;
      })
      .addCase(deleteProjectExpertRequest.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteProjectExpertRequest.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload.id;
        state.projectExperts = state.projectExperts.filter((expert) => expert.projectExpertId !== deletedId);
        toast.success('Expert deleted successfully');
      })
      .addCase(deleteProjectExpertRequest.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload?.message || 'Failed to delete project');
        state.error = action.payload ?? null;
      })
      .addCase(createProjectRoleRequest.pending, (state) => {
        state.loading = true;

        state.error = null;
      })
      .addCase(createProjectRoleRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createProjectRoleRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(updateProjectRoleRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProjectRoleRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateProjectRoleRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(deleteProjectRoleRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProjectRoleRequest.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload?.id;
        if (deletedId) {
          state.projectsRoles = state.projectsRoles.filter((role) => role.id !== deletedId);
        }
      })
      .addCase(deleteProjectRoleRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(createAndPatchClientRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAndPatchClientRequest.fulfilled, (state, action) => {
        state.loading = false;
        const { client, projectId } = action.payload;
        if (state.projectDetail && state.projectDetail.id === projectId) {
          state.projectDetail.client = client;
        }
      })
      .addCase(createAndPatchClientRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(deleteProjectClientRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProjectClientRequest.fulfilled, (state, action) => {
        state.loading = false;
        const { projectId } = action.payload;
        if (state.projectDetail && state.projectDetail.id === projectId) {
          state.projectDetail.client = null;
        }
      })
      .addCase(deleteProjectClientRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(createMeetingSlotsRequest.pending, (state) => {
        state.createmeetingSlots.loading = true;
        state.createmeetingSlots.error = null;
      })
      .addCase(createMeetingSlotsRequest.fulfilled, (state, action) => {
        state.createmeetingSlots.loading = false;
        state.createmeetingSlots.success = true;
        state.createmeetingSlots.data = action.payload;
      })
      .addCase(createMeetingSlotsRequest.rejected, (state, action) => {
        state.createmeetingSlots.loading = false;
        const message = action.payload ?? 'Failed';
        state.createmeetingSlots.error = message;
        toast.error(message);
      })
      .addCase(getUnscheduledMeetingsRequest.pending, (state) => {
        state.getUnscheduledMeetingList.loading = true;
        state.getUnscheduledMeetingList.error = null;
        state.getUnscheduledMeetingList.data = [];
      })
      .addCase(getUnscheduledMeetingsRequest.fulfilled, (state, action) => {
        state.getUnscheduledMeetingList.loading = false;
        state.getUnscheduledMeetingList.success = true;
        const { data } = action.payload;
        state.getUnscheduledMeetingList.data = data;
      })
      .addCase(getUnscheduledMeetingsRequest.rejected, (state, action) => {
        state.createmeetingSlots.loading = false;
        const message = action.payload ?? 'Failed';
        state.getUnscheduledMeetingList.error = message;
        toast.error(message);
      })
      .addCase(generateMeetingRequest.pending, (state) => {
        state.generateMeeting.loading = true;
        state.generateMeeting.error = null;
      })
      .addCase(generateMeetingRequest.fulfilled, (state, action) => {
        state.generateMeeting.loading = false;
        const { authUrl } = action.payload;
        if (authUrl) {
          window.location.href = authUrl;
        }
        state.generateMeeting.success = true;
      })
      .addCase(generateMeetingRequest.rejected, (state, action) => {
        state.generateMeeting.loading = false;
        const message = action.payload ?? 'Failed';
        state.generateMeeting.error = message;
        toast.error(message);
      })
      .addCase(fetchSuggestedTenants.pending, (state) => {
        // Don't set global loading = true for tenant fetching
        state.error = null;
      })
      .addCase(fetchSuggestedTenants.fulfilled, (state, action) => {
        // Don't set global loading = false
        state.tenants = action.payload;
      })
      .addCase(fetchSuggestedTenants.rejected, (state, action) => {
        // Don't set global loading = false
        const message = action.payload || 'Error fetching tenants';
        state.error = { message };
        toast.error(message);
      })
      .addCase(fetchUnassignedProjects.pending, (state) => {
        state.loadingUnassigned = true;
        state.errorUnassigned = null;
      })
      .addCase(fetchUnassignedProjects.fulfilled, (state, action) => {
        state.loadingUnassigned = false;
        state.unassignedProjects = action.payload;
      })
      .addCase(fetchUnassignedProjects.rejected, (state, action) => {
        state.loadingUnassigned = false;
        state.errorUnassigned = action.payload ?? null;
      })
      .addCase(assignProject.pending, (state) => {
        state.loadingAssign = true;
      })
      .addCase(assignProject.fulfilled, (state, action) => {
        state.loadingAssign = false;
        if (state.unassignedProjects) {
          state.unassignedProjects = state.unassignedProjects.filter((p) => p.id !== action.payload.id);
        }
        if (!state.assignedProjects) {
          state.assignedProjects = [];
        }
        state.assignedProjects.push(action.payload);
        if (state.projectDetail && state.projectDetail.id === action.payload.id) {
          state.projectDetail = { ...state.projectDetail, ...action.payload };
        }
      })
      .addCase(assignProject.rejected, (state) => {
        state.loadingAssign = false;
      })
      .addCase(unassignProject.pending, (state) => {
        state.loadingAssign = true; // or you can create a separate loadingUnassign if needed
      })
      .addCase(unassignProject.fulfilled, (state, action) => {
        state.loadingAssign = false;
        if (!state.unassignedProjects) {
          state.unassignedProjects = [];
        }
        state.unassignedProjects.push(action.payload);
        state.assignedProjects = state.assignedProjects.filter((p) => p.id !== action.payload.id);
        if (state.projectDetail && state.projectDetail.id === action.payload.id) {
          state.projectDetail = { ...state.projectDetail, ...action.payload };
        }
      })
      .addCase(unassignProject.rejected, (state) => {
        state.loadingAssign = false;
      });
  },
});

export const { resetError: resetProjectError, resetProjectSessionState } = projectSlice.actions;
